import React, { useEffect, useState } from 'react';
import {
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Container,
    Divider,
    IconButton,
    LinearProgress,
    Link as MuiLink,
    Paper,
    Stack,
    Tab,
    Tabs,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import GitHubIcon from '@mui/icons-material/GitHub';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import ApiService from '../network/API';
import EvaluationList from '../components/EvaluationList';
import YandexAdBlock from '../components/ads/YandexAdBlock';
import CourseworkCard from '../components/CourseworkCard';
import { TelegramConnectRow } from '../components/TelegramConnect';
import { copyToClipboard } from '../copyToClipboard';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

const FEED_BLOCK_ID = "R-A-20141312-5";

const gradeColor = (g) => (g >= 4.5 ? 'success' : g >= 3.5 ? 'primary' : g >= 2.5 ? 'warning' : 'error');

// Данные для входа в Gitea: строка «значок — название — действие», по нажатию раскрываются логин и пароль.
const GitRow = () => {
    const [credentials, setCredentials] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [open, setOpen] = useState(false);

    const toggle = () => {
        if (credentials) {
            setOpen((v) => !v);
            return;
        }
        setLoading(true);
        setError(null);
        ApiService.getGitCredentials()
            .then((c) => {
                setCredentials(c);
                setOpen(true);
            })
            .catch((err) => setError(err.reason || err.message || 'Не удалось получить данные'))
            .finally(() => setLoading(false));
    };

    const field = (label, value) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ width: 56 }}>{label}</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1, wordBreak: 'break-all' }}>{value}</Typography>
            <Tooltip title="Скопировать">
                <IconButton size="small" onClick={() => copyToClipboard(value)}><ContentCopyIcon fontSize="small" /></IconButton>
            </Tooltip>
        </Box>
    );

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{
                    width: 40, height: 40, borderRadius: 1.5, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: (t) => alpha(t.palette.text.primary, 0.1),
                }}>
                    <GitHubIcon />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={600} lineHeight={1.3}>Git (Gitea)</Typography>
                    <Typography variant="body2" color="text.secondary">логин и пароль для git-задач</Typography>
                </Box>
                <Button size="small" variant="outlined" onClick={toggle} disabled={loading} sx={{ flexShrink: 0 }}>
                    {loading ? <CircularProgress size={16} /> : open ? 'Скрыть' : 'Показать'}
                </Button>
            </Box>
            {error && <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>{error}</Typography>}
            <Collapse in={open} unmountOnExit>
                {credentials && (
                    <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1.5, bgcolor: (t) => alpha(t.palette.text.primary, 0.05) }}>
                        {field('Логин', credentials.username)}
                        {field('Пароль', credentials.password)}
                        {credentials.giteaBaseUrl && (
                            <MuiLink href={credentials.giteaBaseUrl} target="_blank" rel="noopener" variant="body2"
                                     sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                {credentials.giteaBaseUrl} <OpenInNewIcon sx={{ fontSize: 16 }} />
                            </MuiLink>
                        )}
                    </Box>
                )}
            </Collapse>
        </Box>
    );
};

export const ProfilePage = () => {
    const [evaluationsData, setEvaluationsData] = useState([]);
    const [profile, setProfile] = useState(null);
    const [currentTag, setCurrentTag] = useState(null);
    const [evaluationsLoaded, setEvaluationsLoaded] = useState(false);
    const [articlesByTag, setArticlesByTag] = useState({});
    const [courseworks, setCourseworks] = useState([]);

    const loadCourseworks = () => {
        ApiService.getMyCourseworks().then(setCourseworks).catch(() => {});
    };

    useEffect(() => {
        ApiService.getEvaluations().then((res) => {
            setEvaluationsData(res);
            if (res.length > 0) {
                setCurrentTag(res[0].tag);
            }
            setEvaluationsLoaded(true);
        });
        ApiService.getMeUser().then((res) => setProfile(res));
        loadCourseworks();
    }, []);

    // Лекции без оценки тоже должны быть видны (как в таблице группы у преподавателя),
    // поэтому подтягиваем полный список лекций тега, а не только те, где уже есть оценка.
    useEffect(() => {
        if (!currentTag || articlesByTag[currentTag.id]) return;

        ApiService.getAllArticles([currentTag.id], [], 0, 100)
            .then((articles) => {
                setArticlesByTag((prev) => ({ ...prev, [currentTag.id]: articles }));
            })
            .catch(() => {
                setArticlesByTag((prev) => ({ ...prev, [currentTag.id]: [] }));
            });
    }, [currentTag, articlesByTag]);

    const handleTagChange = (event, newValue) => {
        const selectedTag = evaluationsData.find(item => item.tag.id === newValue)?.tag;
        setCurrentTag(selectedTag);
    };

    if (!profile || !evaluationsLoaded) {
        return (
            <Box display="flex" justifyContent="center" py={10}>
                <CircularProgress />
            </Box>
        );
    }

    const currentTagData = evaluationsData.find(item => item.tag.id === currentTag?.id);
    const currentTagArticles = currentTag ? articlesByTag[currentTag.id] : undefined;

    const evaluationByArticleId = {};
    (currentTagData?.evaluations || []).forEach((e) => {
        evaluationByArticleId[e.article.id] = e;
    });

    // Полный список: у лекций без оценки просто нет записи в evaluationByArticleId —
    // EvaluationList покажет их с прочерком вместо пропуска.
    const currentEvaluations = currentTagArticles
        ? currentTagArticles.map((article) => evaluationByArticleId[article.id] || { article, evaluation: null })
        : (currentTagData?.evaluations || []);

    const gradedCount = currentTagData ? currentTagData.evaluations.length : 0;
    const totalCount = currentTagArticles ? currentTagArticles.length : null;
    const average = currentTagData?.averageGrade;

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '340px minmax(0, 1fr)' }, gap: 3, alignItems: 'start' }}>
                {/* Левая колонка: профиль и подключения */}
                <Stack spacing={2}>
                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <Box sx={{
                            height: 88,
                            background: (t) => `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.85)} 0%, ${alpha(t.palette.secondary.main, 0.7)} 100%)`,
                        }} />
                        <Box sx={{ px: 3, pb: 3, mt: -5.5 }}>
                            <Avatar
                                src={profile.avatarFileName ? ApiService.getFileUrl(profile.avatarFileName) : undefined}
                                alt={profile.username}
                                sx={{ width: 88, height: 88, border: (t) => `4px solid ${t.palette.background.paper}`, bgcolor: 'action.selected' }}
                            />
                            <Typography variant="h5" fontWeight={700} sx={{ mt: 1.5 }} noWrap>{profile.username}</Typography>
                            {profile.description && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{profile.description}</Typography>
                            )}
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                                {profile.group?.name && <Chip size="small" color="primary" icon={<GroupsOutlinedIcon />} label={profile.group.name} />}
                                {profile.createdAt && (
                                    <Chip size="small" variant="outlined" label={`С нами с ${format(new Date(profile.createdAt), 'd MMMM yyyy', { locale: ru })}`} />
                                )}
                                {profile.countArticles > 0 && <Chip size="small" variant="outlined" label={`Статей: ${profile.countArticles}`} />}
                            </Stack>
                        </Box>
                    </Paper>

                    <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5 }}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>Подключения</Typography>
                        <Stack spacing={2} divider={<Divider />}>
                            <TelegramConnectRow />
                            <GitRow />
                        </Stack>
                    </Paper>
                </Stack>

                {/* Правая колонка: практика и успеваемость */}
                <Stack spacing={3} sx={{ minWidth: 0 }}>
                    {courseworks.length > 0 && (
                        <Box>
                            {courseworks.map((submission) => (
                                <CourseworkCard
                                    key={submission.courseworkId}
                                    submission={submission}
                                    onSaved={loadCourseworks}
                                />
                            ))}
                        </Box>
                    )}

                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
                            <Typography variant="h6" fontWeight={700}>Успеваемость</Typography>
                        </Box>

                        {evaluationsData.length === 0 ? (
                            <Typography variant="body1" color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>
                                Пока нет оценок ни по одному предмету
                            </Typography>
                        ) : (
                            <>
                                <Tabs
                                    value={currentTag?.id || false}
                                    onChange={handleTagChange}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    sx={{ px: 1, borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 44 } }}
                                >
                                    {evaluationsData.map((item) => (
                                        <Tab
                                            key={item.tag.id}
                                            value={item.tag.id}
                                            label={
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    {item.tag.name}
                                                    {item.exam && <Chip label="Экзамен" size="small" color="primary" variant="outlined" />}
                                                </Box>
                                            }
                                        />
                                    ))}
                                </Tabs>

                                <Box sx={{ p: 2.5 }}>
                                    {currentTagData && (
                                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
                                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                                <Typography variant="body2" color="text.secondary">Оценено лекций</Typography>
                                                <Typography variant="h5" fontWeight={700}>
                                                    {gradedCount}
                                                    {totalCount != null && <Typography component="span" variant="body1" color="text.secondary"> из {totalCount}</Typography>}
                                                </Typography>
                                                {totalCount > 0 && (
                                                    <LinearProgress variant="determinate" value={Math.min(100, Math.round((gradedCount / totalCount) * 100))}
                                                                    sx={{ height: 6, borderRadius: 3, mt: 1 }} />
                                                )}
                                            </Paper>
                                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                                <Typography variant="body2" color="text.secondary">Средний балл</Typography>
                                                {average != null ? (
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        <Typography variant="h5" fontWeight={700}>{average.toFixed(1)}</Typography>
                                                        <Chip size="small" color={gradeColor(average)} label={average >= 4.5 ? 'отлично' : average >= 3.5 ? 'хорошо' : average >= 2.5 ? 'удовл.' : 'нужно подтянуть'} />
                                                    </Stack>
                                                ) : (
                                                    <Typography variant="h5" fontWeight={700} color="text.disabled">—</Typography>
                                                )}
                                            </Paper>
                                        </Box>
                                    )}
                                    <EvaluationList items={currentEvaluations} />
                                </Box>
                            </>
                        )}
                    </Paper>
                </Stack>
            </Box>
            <Box sx={{ mt: 3 }}>
                <YandexAdBlock blockId={FEED_BLOCK_ID} type="feed"/>
            </Box>
        </Container>
    );
};
